import React, { useState, useMemo } from 'react';
import { Stethoscope } from 'lucide-react';
import CalculatorLayout from '../../components/calculators/CalculatorLayout';
import { HeightInput, WeightInput } from '../../components/calculators/MeasureInputs';
import { EmptyResult, PrimaryResult, StatRow } from '../../components/calculators/ResultCard';
import { calculateBSA } from '../../lib/calculatorUtils';

const BSACalculatorPage = () => {
  const [unit, setUnit] = useState('metric');
  const [heightCm, setHeightCm] = useState('');
  const [weightKg, setWeightKg] = useState('');

  const result = useMemo(() => {
    const h = parseFloat(heightCm);
    const w = parseFloat(weightKg);
    if (!h || !w || h < 50 || w < 5) return null;
    return calculateBSA(h, w);
  }, [heightCm, weightKg]);

  const inputs = (
    <div className="space-y-5" data-testid="bsa-inputs">
      <h2 className="text-xl sm:text-2xl font-bold text-white mb-1">Enter Your Details</h2>
      <p className="text-slate-400 text-sm mb-5">We use the Mosteller formula, which is widely used in clinical practice.</p>
      <HeightInput unit={unit} onUnitChange={setUnit} heightCm={heightCm} onHeightCmChange={setHeightCm} />
      <WeightInput unit={unit} onUnitChange={setUnit} weightKg={weightKg} onWeightKgChange={setWeightKg} />
    </div>
  );

  const resultNode = result ? (
    <div className="flex flex-col h-full">
      <PrimaryResult label="Body Surface Area" value={result} unit="m²" accent="from-sky-500 to-teal-500" testId="bsa-result-value" />
      <div className="mt-8 space-y-1">
        <StatRow label="Average adult (male)" value="1.9" unit="m²" />
        <StatRow label="Average adult (female)" value="1.6" unit="m²" />
        <StatRow label="Average newborn" value="0.25" unit="m²" />
      </div>
      <p className="text-slate-500 text-xs mt-6 leading-relaxed">
        BSA is used in clinical settings for medication dosing, cardiac index, and ICU calculations. This is for educational use only, not a substitute for clinical assessment.
      </p>
    </div>
  ) : (
    <EmptyResult message="Enter height and weight to compute Body Surface Area." />
  );

  return (
    <CalculatorLayout
      slug="bsa" title="Body Surface Area Calculator"
      subtitle="Calculate Body Surface Area (BSA) using the Mosteller formula, a medical-grade reference."
      metaDescription="Body Surface Area Calculator using the Mosteller formula for BSA in m², used in clinical assessments."
      icon={Stethoscope}
      accentGradient="from-sky-500 to-teal-500" accentColor="text-sky-400"
      badgeBg="bg-sky-500/10" badgeBorder="border-sky-500/30"
      inputs={inputs} result={resultNode}
      formulaText={<>
        <p><strong className="text-slate-300">Mosteller:</strong> BSA (m²) = √((height_cm × weight_kg) / 3600)</p>
      </>}
    />
  );
};

export default BSACalculatorPage;
